import { forwardRef, InputHTMLAttributes, ReactNode, useState } from "react";
import { Icon } from "../ui";

export const inputClass =
  "w-full h-12 px-4 rounded-lg bg-surface-container-lowest border border-outline-variant text-on-surface font-body-md text-body-md placeholder:text-outline outline-none transition-shadow focus:border-primary-container focus:ring-4 focus:ring-primary-container/15";

type FieldProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  error?: string;
  trailing?: ReactNode;
};

// Labelled text input matching the landing page design
export const Field = forwardRef<HTMLInputElement, FieldProps>(
  ({ label, error, id, trailing, ...props }, ref) => (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <label
          htmlFor={id}
          className="font-label-md text-label-md text-on-surface"
        >
          {label}
        </label>
        {trailing}
      </div>
      <input
        ref={ref}
        id={id}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : undefined}
        className={`${inputClass} ${error ? "border-error focus:border-error focus:ring-error/15" : ""}`}
        {...props}
      />
      {error && (
        <span id={`${id}-error`} className="font-body-sm text-xs text-error">
          {error}
        </span>
      )}
    </div>
  ),
);

// Password input with a show/hide toggle
export const PasswordField = forwardRef<HTMLInputElement, FieldProps>(
  ({ label, error, id, trailing, ...props }, ref) => {
    const [visible, setVisible] = useState(false);
    return (
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <label
            htmlFor={id}
            className="font-label-md text-label-md text-on-surface"
          >
            {label}
          </label>
          {trailing}
        </div>
        <div className="relative">
          <input
            ref={ref}
            id={id}
            type={visible ? "text" : "password"}
            aria-invalid={!!error}
            aria-describedby={error ? `${id}-error` : undefined}
            className={`${inputClass} pr-12 ${error ? "border-error focus:border-error focus:ring-error/15" : ""}`}
            {...props}
          />
          <button
            type="button"
            onClick={() => setVisible((v) => !v)}
            aria-label={visible ? "Hide password" : "Show password"}
            className="absolute right-1 top-1 h-10 w-10 rounded-lg flex items-center justify-center text-on-surface-variant hover:text-primary hover:bg-surface-container-low transition-colors"
          >
            <Icon
              name={visible ? "visibility_off" : "visibility"}
              className="text-[20px]"
            />
          </button>
        </div>
        {error && (
          <span id={`${id}-error`} className="font-body-sm text-xs text-error">
            {error}
          </span>
        )}
      </div>
    );
  },
);

export const SubmitButton = ({
  loading,
  children,
}: {
  loading: boolean;
  children: ReactNode;
}) => (
  <button
    type="submit"
    disabled={loading}
    className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-xl bg-primary text-on-primary font-label-lg text-label-lg shadow-lg shadow-primary/25 hover:bg-primary-container transition-all disabled:opacity-70 disabled:cursor-wait"
  >
    {loading ? (
      <span
        className="w-5 h-5 rounded-full border-2 border-on-primary/40 border-t-on-primary animate-spin"
        aria-label="Loading"
      />
    ) : (
      children
    )}
  </button>
);

export const FormAlert = ({
  tone,
  children,
}: {
  tone: "error" | "success";
  children: ReactNode;
}) => (
  <div
    role={tone === "error" ? "alert" : "status"}
    className={`flex items-start gap-2.5 p-3 rounded-xl font-body-sm text-body-sm ${
      tone === "error"
        ? "bg-error-container text-on-error-container"
        : "bg-tertiary-fixed/30 text-tertiary"
    }`}
  >
    <Icon
      name={tone === "error" ? "error" : "check_circle"}
      className="text-[20px] shrink-0"
    />
    <span>{children}</span>
  </div>
);

export const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z"
    />
    <path
      fill="#34A853"
      d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z"
    />
    <path
      fill="#FBBC05"
      d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z"
    />
    <path
      fill="#EA4335"
      d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z"
    />
  </svg>
);
